/*
 * Nexus Elite Solutions - 2nd October 2026, revised 5th October 2026
 * Cart drawer: audit point 4 (undo a removal) and point 8 (change size/option
 * inside the cart).
 *
 * HOW THIS TALKS TO THE THEME
 * theme.js is an ES module and exports Cart rather than putting it on window,
 * so the handle is customElements.get('cart-element'). Everything here goes
 * through the theme's own funnel instead of re-implementing it:
 *   Cart.addItems(init, trigger) posts /cart/add.js, updates the basket and
 *     the header count, then re-renders every cart view.
 *   Cart.sections MUST be in the request body or nothing re-renders.
 *   Cart.fillBasket / updateTotals / updateAllHtml are the three statics the
 *     theme itself calls after a successful /cart/change.js.
 *   theme:cart:updated fires once per cart-element after the swap.
 *
 * WHAT SURVIVES AN UPDATE
 * The theme replaces .cart--body and .cart--totals only. The undo bar sits
 * outside both so it survives the very update it reports on. The variant
 * selects live inside .cart--body and are rebuilt every time - which is why
 * every listener here is delegated from cart-element, never bound to controls.
 *
 * ---------------------------------------------------------------------------
 * 5th October 2026 - two defects the client reported, both fixed below.
 *
 * (1) REMOVING A LINE SILENTLY DID NOTHING, AND UNDO THEN DOUBLED IT.
 * The theme's remove button calls tryToUpdateQuantity(line, 0), and every
 * change runs through this clamp in updateQuantity:
 *
 *     o = parseInt(i.dataset.quantity)        // the variant's AVAILABLE stock
 *     h = this.getOtherVariantQuantities(i, n)
 *     l = o < e + h && 'shopify' === s && 'continue' !== r
 *     l && (e = o - h)
 *
 * The clamp exists to stop a shopper asking for more than exists. It runs on a
 * REMOVAL too, where e is 0 - and a removal can never exceed stock, so it has
 * no business running. When it fires it rewrites the requested 0 into o - h, so
 * the line is not removed; and because tryToUpdateQuantity suppresses the error
 * whenever the requested quantity was 0 (`|| 0 === e ||`), the shopper is told
 * nothing. The line stays, looking untouched. Press Undo and the captured
 * quantity is added on top of the line that never left - two becomes four.
 *
 * So removals no longer go through tryToUpdateQuantity. They are posted here
 * with quantity 0 and handed to the theme's own three statics, which is what
 * updateQuantity does on success anyway: same re-render, same header count,
 * same events, minus the clamp. The click is stopped in the capture phase so
 * the theme's handler cannot clamp it a second time. Quantity INCREASES are
 * untouched and still go through the theme, where the clamp does real work.
 *
 * (2) NO UNDO WHEN THE LAST ITEM WAS REMOVED.
 * Emptying the cart puts data-cart-empty="true" on .layout--viewport and the
 * theme's stylesheet then hides the cart scaffold, showing .cart--empty in its
 * place - so the bar was being set to aria-hidden="false" inside a container
 * nobody could see. Fighting that with !important would not help if an
 * ancestor is the hidden thing, so instead the bar moves into .cart--empty
 * while the cart is empty and returns after .cart--header once it is not.
 * .cart--empty is visible by definition in that state and sits outside
 * .cart--body, so no swap destroys it. The bar is re-anchored to .cart--header
 * rather than a remembered nextSibling, because that sibling is usually
 * .cart--body, which every update replaces.
 */
(function () {
  'use strict';

  var UNDO_MS = 8000;
  var busy = false;
  var snapshot = [];
  var count = 0;
  var pending = null;
  var timer = null;

  function CartClass() {
    return window.customElements && window.customElements.get('cart-element');
  }

  function cartUrl() {
    var r = (window.Shopify && window.Shopify.routes) || {};
    return (r.cart || '/cart') + '.js';
  }

  function changeUrl() {
    var r = (window.Shopify && window.Shopify.routes) || {};
    return (r.cart_change || '/cart/change') + '.js';
  }

  /* A snapshot of the real cart, refreshed after every update. The DOM does not
     expose line item properties or a selling plan in a usable form, so an undo
     that relied on markup alone would silently drop a personalised engraving. */
  function refresh() {
    return fetch(cartUrl(), { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        if (cart && cart.items) {
          snapshot = cart.items;
          count = cart.item_count;
        }
      })
      .catch(function () {});
  }

  function lineAt(num) {
    var i = parseInt(num, 10) - 1;
    return i >= 0 && snapshot[i] ? snapshot[i] : null;
  }

  function payload(line, quantity) {
    var item = { id: line.variant_id || line.id, quantity: quantity };
    if (line.properties && Object.keys(line.properties).length) item.properties = line.properties;
    if (line.selling_plan_allocation && line.selling_plan_allocation.selling_plan) {
      item.selling_plan = line.selling_plan_allocation.selling_plan.id;
    }
    return item;
  }

  function addItem(item) {
    var Cart = CartClass();
    if (!Cart) return Promise.reject();
    return Cart.addItems({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [item], sections: Cart.sections })
    }, null);
  }

  /* Remove a line for real - see note (1) at the top of this file. The three
     statics at the end are the same ones the theme calls after its own
     successful change, in the same order, so the drawer, the cart page, the
     totals and the header count all land exactly where they normally would. */
  function removeLine(lineNum) {
    var Cart = CartClass();
    if (!Cart) return Promise.reject(new Error('cart-element not defined'));

    return fetch(changeUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: lineNum, quantity: 0, sections: Cart.sections })
    })
      .then(function (res) {
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      })
      .then(function (cart) {
        Cart.fillBasket(cart.items);
        Cart.updateTotals(cart.item_count);
        Cart.updateAllHtml(cart.sections);
      });
  }

  /* ---- point 4: undo ---- */

  function bars() {
    return document.querySelectorAll('.cart--undo');
  }

  function hideUndo() {
    if (timer) { clearTimeout(timer); timer = null; }
    pending = null;
    Array.prototype.forEach.call(bars(), function (bar) {
      bar.setAttribute('aria-hidden', 'true');
    });
  }

  function showUndo(title) {
    Array.prototype.forEach.call(bars(), function (bar) {
      var text = bar.querySelector('.cart--undo-text');
      if (text) text.textContent = title ? title + ' removed' : 'Item removed';
      bar.setAttribute('aria-hidden', 'false');
    });
    if (timer) clearTimeout(timer);
    timer = setTimeout(hideUndo, UNDO_MS);
  }

  /* Keep the bar where it can actually be seen - see note (2) at the top. */
  function placeBars() {
    var empty = count === 0;

    Array.prototype.forEach.call(bars(), function (bar) {
      var root = bar.closest('.cart--root');
      if (!root) return;

      if (empty) {
        var emptyBlock = root.querySelector('.cart--empty');
        if (emptyBlock && bar.parentNode !== emptyBlock) {
          emptyBlock.insertBefore(bar, emptyBlock.firstChild);
        }
        return;
      }

      var header = root.querySelector('.cart--header');
      if (header && header.parentNode && bar.previousElementSibling !== header) {
        header.parentNode.insertBefore(bar, header.nextSibling);
      }
    });
  }

  /* Capture phase on purpose, and now for two reasons: it still runs before the
     theme and reads the line while the markup exists, and stopping propagation
     here is what keeps the theme's clamped remove handler from running at all.
     Raw addEventListener, not the theme's window.on helper - that helper keeps
     one handler per event name and would clobber another binding. */
  function captureRemoval(event) {
    if (!event.target || !event.target.closest) return;
    if (!event.target.closest('.quantity-selector--remove')) return;

    var row = event.target.closest('.cart--item');
    if (!row) return;

    var lineNum = row.getAttribute('data-line-num');
    var line = lineAt(lineNum);
    if (!line) return;

    /* From here the removal is ours, so the theme must not also handle it. */
    event.preventDefault();
    event.stopPropagation();

    if (busy) return;
    busy = true;

    pending = {
      item: payload(line, line.quantity),
      title: row.getAttribute('data-title') || line.product_title || '',
      shown: false
    };

    removeLine(lineNum)
      .catch(function () { pending = null; })
      .then(function () { busy = false; });
  }

  function restore() {
    if (!pending || busy) return;
    busy = true;
    var item = pending.item;
    hideUndo();
    addItem(item)
      .catch(function () {})
      .then(function () { busy = false; });
  }

  /* ---- point 8: change the size or option in place ---- */

  /* Shopify has no "swap this line's variant" call, so it is add-then-remove.
     That ORDER matters: if the add fails - sold out, inventory limit - the
     original line is still sitting in the cart untouched. Removing first would
     have risked emptying the line and then failing to put anything back.
     The removal uses removeLine for the same reason as the remove button: the
     theme's clamp would otherwise leave the old size behind alongside the new
     one, which is the defect in note (1) wearing a different hat. */
  function changeVariant(select) {
    if (busy) return;

    var lineNum = select.getAttribute('data-line');
    var currentId = select.getAttribute('data-current-id');
    var newId = select.value;

    if (!lineNum || !newId || newId === currentId) return;

    var line = lineAt(lineNum);
    if (!line) return;

    busy = true;
    select.disabled = true;

    var item = payload(line, line.quantity);
    item.id = parseInt(newId, 10);

    addItem(item)
      .then(function () { return removeLine(lineNum); })
      .catch(function () {
        select.value = currentId;
        select.disabled = false;
      })
      .then(function () { busy = false; });
  }

  /* ---- wiring ---- */

  function bind(root) {
    if (root.dataset.cartEditBound === 'true') return;
    root.dataset.cartEditBound = 'true';

    root.addEventListener('click', captureRemoval, true);

    root.addEventListener('click', function (event) {
      if (event.target && event.target.closest && event.target.closest('.cart--undo-button')) {
        event.preventDefault();
        restore();
      }
    });

    root.addEventListener('change', function (event) {
      var select = event.target && event.target.closest
        ? event.target.closest('.cart--item--variant-select')
        : null;
      if (select) changeVariant(select);
    });
  }

  function bindAll() {
    Array.prototype.forEach.call(document.querySelectorAll('cart-element'), bind);
  }

  function init() {
    bindAll();
    refresh().then(placeBars);

    window.addEventListener('theme:cart:updated', function () {
      bindAll();
      refresh().then(function () {
        placeBars();
        if (pending && !pending.shown) {
          pending.shown = true;
          showUndo(pending.title);
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();